import { cloneDeep } from 'lodash'
import formatImages from './formatImages'
import formatLeadPromoDesc from './formatLeadPromoDesc'
import formatLandPrograms from './formatLandPrograms'
import formatItinerary from './formatItinerary'

export default function formatPackage(cruise) {
  const cruiseData = cloneDeep(cruise || {})

  cruiseData.identifier = `${cruise.id}-${cruise.sailDate}`

  // Because we pass the startDate and sailDate when we create cruises but our land programs don't
  // appear in our itineraries we need to pass the sail date when we have land programs.
  let itineraryStartDate = cruise.startDate
  if (cruise.landPrograms && Object.keys(cruise.landPrograms).length) {
    itineraryStartDate = cruise.sailDate
  }

  cruiseData.itinerary = formatItinerary(
    cruise.itinerary,
    itineraryStartDate,
    cruise.landPrograms
  )

  if (
    cruise.pricing &&
    cruise.pricing.pricing &&
    cruise.occupancy &&
    cruise.selections
  ) {
    cruiseData.stateRoomData = formatPricingFromSelections(
      cruise.pricing,
      cruise.occupancy,
      cruise.selections,
      cruise.brand,
      cruise.groupId
    )
  }

  let landPrograms

  if (
    cruise.selections &&
    cruise.selections.landPrograms &&
    cruise.landPrograms
  ) {
    const rawLandPrograms = cruise.selections.landPrograms.reduce(
      (obj, programId) => {
        if (cruise.landPrograms[programId]) {
          obj[programId] = cruise.landPrograms[programId]
        }
        return obj
      },
      {}
    )
    landPrograms = formatLandPrograms(
      rawLandPrograms,
      cruise.sailDate,
      cruise.sailingNights
    )
    cruiseData.landPrograms = landPrograms
  }

  if (landPrograms) {
    cruiseData.landProgramTotalPrice = Object.values(landPrograms).reduce(
      (total, program) => {
        total += Number(program.priceAmount)
        return total
      },
      0
    )
  }

  return cruiseData
}

export const formatPricingFromSelections = (
  pricingData,
  guestCount,
  selections,
  brand,
  groupId
) => {
  const guestCountPricing = formatGuestCountPricing(
    pricingData,
    guestCount,
    brand,
    groupId
  )

  const { categories } = pricingData
  let bestRate = null
  const selectedStateRooms = Object.entries(selections).reduce(
    (arr, [selectionKey, selectionCodesArray]) => {
      selectionCodesArray.forEach(selectionCode => {
        const isSuperCategory = selectionKey === 'superCategories'
        if (isSuperCategory) {
          const matchingData =
            guestCountPricing[selectionCode] &&
            guestCountPricing[selectionCode].bestGuestCountRate

          if (matchingData) {
            const superCategoryData =
              (categories && categories[`_${selectionCode}`]) || {}

            const formattedCat = {
              ...matchingData,
              superCategoryData: {
                ...superCategoryData,
                image: formatImages(superCategoryData.image)
              },
              isSuperCategory: true,
              superCategoryCode: selectionCode
            }
            if (
              !bestRate ||
              bestRate[guestCount].netPrice > formattedCat.netPrice
            ) {
              bestRate = formattedCat.pricing
            }
            arr.push(formattedCat)
          }
        } else {
          const selectionIsRefundableRate =
            selectionKey === 'refundableCategories'
          let matchingStateRoom

          for (const [superCatKey, superCatPricing] of Object.entries(
            guestCountPricing
          )) {
            if (matchingStateRoom) break
            if (superCatPricing.availableStateRooms) {
              for (const price of superCatPricing.availableStateRooms) {
                if (
                  price.stateroomCategoryCode === selectionCode &&
                  price.isRefundable === selectionIsRefundableRate
                ) {
                  matchingStateRoom = {
                    ...price,
                    superCategoryCode: superCatKey,
                    isSuperCategory: false
                  }
                  break
                }
              }
            }
          }

          if (matchingStateRoom) {
            arr.push(matchingStateRoom)
            if (
              !bestRate ||
              bestRate[guestCount].netPrice > matchingStateRoom.netPrice
            ) {
              bestRate = matchingStateRoom.pricing
            }
          }
        }
      })
      return arr
    },
    []
  )

  return {
    selectedStateRooms,
    bestRate,
    guestCountPricing
  }
}

export function formatGuestCountPricing(
  pricingData,
  guestCount,
  brand,
  groupId
) {
  if (!pricingData || !pricingData.pricing) {
    return { isSoldOut: true }
  }

  const { pricing, categories, staterooms } = pricingData

  if (
    !pricing ||
    !pricing[guestCount] ||
    !Object.keys(pricing[guestCount]).length
  ) {
    return { isSoldOut: true }
  }

  const currentPrice = Object.values(pricing[guestCount])

  return currentPrice.reduce((obj, superCategory) => {
    if (!obj[superCategory.stateroomType]) {
      const matchingCategory =
        (categories && categories[superCategory.category]) || {}
      const category = {
        ...matchingCategory,
        image: formatImages(matchingCategory.image)
      }

      obj[superCategory.stateroomType] = {
        category,
        categoryName: matchingCategory.name,
        stateroomType: superCategory.stateroomType,
        stateroomDescription: superCategory.stateroomDescription,
        bestGuestCountRate: null,
        availableStateRooms: []
      }
    }

    const isGroupPricing = groupId && superCategory?.groups?.[groupId]
    const refundableCategories = isGroupPricing
      ? superCategory?.groups?.[groupId].refundableCategories
      : superCategory.refundableCategories
    const nonRefundableCategories = isGroupPricing
      ? superCategory?.groups?.[groupId].nonRefundableCategories
      : superCategory.nonRefundableCategories

    for (const stateRoom of [
      ...refundableCategories,
      ...nonRefundableCategories
    ]) {
      const formattedGuestPricing = formatOccupancyPricing(
        stateRoom,
        guestCount,
        brand
      )

      const matchingStateRoom = staterooms && staterooms[stateRoom.stateroom]

      const formattedStateRoom = {
        ...stateRoom,
        stateroom: {
          ...matchingStateRoom,
          image:
            matchingStateRoom && matchingStateRoom.image
              ? formatImages(matchingStateRoom.image)
              : []
        },
        isRefundable: stateRoom.refundability === 'REFUNDABLE',
        pricing: formattedGuestPricing
      }
      obj[superCategory.stateroomType].availableStateRooms.push(
        formattedStateRoom
      )

      if (
        !obj[superCategory.stateroomType].bestGuestCountRate ||
        obj[superCategory.stateroomType].bestGuestCountRate.netPrice >
          stateRoom.netPrice
      ) {
        obj[superCategory.stateroomType].bestGuestCountRate = formattedStateRoom
      }
    }

    return obj
  }, {})
}

function formatOccupancyPricing(stateRoom, occupancy, brand) {
  const { guestPrices } = stateRoom

  const guestCount = Number(occupancy)

  if (guestCount === 1 || guestCount === 2) {
    return formatGuestPriceArray(
      [guestCount],
      guestPrices,
      occupancy,
      stateRoom
    )
  }

  if (guestCount === 3) {
    return formatGuestPriceArray([2, 3], guestPrices, occupancy, stateRoom)
  }

  // Celebrity requires 3rd and 4th pricing to always be displayed individually
  if (brand === 'C') {
    return formatGuestPriceArray([2, 3, 4], guestPrices, occupancy, stateRoom)
  }

  //  guestPrices.guestPrice are an array so the index position of 4th pricing is 3 etc.
  if (
    guestPrices &&
    guestPrices.guestPrice &&
    guestPrices.guestPrice[2] &&
    guestPrices.guestPrice[3]
  ) {
    const fourthPricing =
      guestPrices.guestPrice[3] &&
      guestPrices.guestPrice[3].priceInfos &&
      guestPrices.guestPrice[3].priceInfos.priceInfo
        ? guestPrices.guestPrice[3].priceInfos.priceInfo.find(
            price => price.priceTypeCode === 'DISCOUNTED_CRUISE_FARE'
          )
        : null
    const thirdPricing =
      guestPrices.guestPrice[2] &&
      guestPrices.guestPrice[2].priceInfos &&
      guestPrices.guestPrice[2].priceInfos.priceInfo
        ? guestPrices.guestPrice[2].priceInfos.priceInfo.find(
            price => price.priceTypeCode === 'DISCOUNTED_CRUISE_FARE'
          )
        : null
    return fourthPricing &&
      thirdPricing &&
      fourthPricing.amount === thirdPricing.amount
      ? formatGuestPriceArray([2, 4], guestPrices, occupancy, stateRoom)
      : formatGuestPriceArray([2, 3, 4], guestPrices, occupancy, stateRoom)
  }

  return null
}

function getGuestPriceData(guestPrices, priceIndex) {
  return guestPrices &&
    guestPrices.guestPrice &&
    guestPrices.guestPrice[priceIndex] &&
    guestPrices.guestPrice[priceIndex].priceInfos &&
    guestPrices.guestPrice[priceIndex].priceInfos.priceInfo
    ? guestPrices.guestPrice[priceIndex].priceInfos.priceInfo.find(
        price => price.priceTypeCode === 'DISCOUNTED_CRUISE_FARE'
      )
    : null
}

function formatGuestPriceArray(arr, guestPrices, guestCount, stateRoom) {
  return arr.reduce((obj, occupancyKey) => {
    const priceIndex = occupancyKey - 1
    const { leadPromoDesc, promos, creditAmt, priceInfo, netPrice } = stateRoom
    if (occupancyKey === 2) {
      const singlePricing = getGuestPriceData(guestPrices, 0)
      const doublePricing = getGuestPriceData(guestPrices, 1)

      const displayPrice = (singlePricing.amount + doublePricing.amount) / 2
      obj[occupancyKey] = {
        displayPrice,
        displayName: '1st/2nd',
        countsFor: 2,
        promos,
        leadPromoDesc: formatLeadPromoDesc(leadPromoDesc, promos),
        creditAmt: creditAmt / guestCount,
        priceInfo
      }
    } else {
      const occupancyPrice = getGuestPriceData(guestPrices, priceIndex)
      obj[occupancyKey] = {
        displayPrice: occupancyPrice.amount,
        displayName:
          occupancyKey === 1
            ? 'Single'
            : occupancyKey === 3
            ? '3rd'
            : arr.some(key => key === 3)
            ? '4th'
            : '3rd/4th',
        countsFor:
          occupancyKey === 1
            ? 1
            : occupancyKey === 3
            ? 1
            : arr.some(key => key === 3)
            ? 1
            : 2,
        leadPromoDesc: formatLeadPromoDesc(leadPromoDesc, promos),
        creditAmt: creditAmt / guestCount,
        priceInfo
      }
      if (Number(guestCount) === 1) {
        const { leadPromoDesc, promos, creditAmt } = stateRoom
        obj[occupancyKey] = {
          ...obj[occupancyKey],
          promos,
          leadPromoDesc: formatLeadPromoDesc(leadPromoDesc, promos),
          creditAmt
        }
      }
    }
    // we need this to display the best rate
    if (Number(occupancyKey) === Number(guestCount)) {
      obj[occupancyKey].netPrice = netPrice
    }

    return obj
  }, {})
}
